import { ApiError } from "@bw/shared/api";

import type { TournamentSanction, TournamentSanctionStatus } from "./api";

export const STATUS_LABEL: Record<TournamentSanctionStatus, string> = {
  requested: "Requested",
  changes_requested: "Changes requested",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  sanctioned: "Sanctioned",
  results_submitted: "Results submitted",
  ratings_applied: "Ratings applied",
  completed_unrated: "Closed without ratings",
  revoked: "Revoked",
};

export type StatusTone = "warning" | "success" | "error" | "info" | "default";

export const STATUS_TONE: Record<TournamentSanctionStatus, StatusTone> = {
  requested: "warning",
  changes_requested: "info",
  rejected: "error",
  withdrawn: "default",
  sanctioned: "success",
  results_submitted: "warning",
  ratings_applied: "success",
  completed_unrated: "default",
  revoked: "error",
};

/** A queue filter: one backend status, or sanctioned events past their results deadline. */
export interface QueueFilter {
  readonly id: string;
  readonly label: string;
  readonly status?: TournamentSanctionStatus;
  readonly overdue?: true;
}

export type SanctionView = "requests" | "events";

export const VIEW_FILTERS: Record<SanctionView, readonly QueueFilter[]> = {
  requests: [
    { id: "requested", label: "Requested", status: "requested" },
    {
      id: "changes_requested",
      label: "Changes requested",
      status: "changes_requested",
    },
    { id: "rejected", label: "Rejected", status: "rejected" },
    { id: "withdrawn", label: "Withdrawn", status: "withdrawn" },
  ],
  events: [
    { id: "sanctioned", label: "Sanctioned", status: "sanctioned" },
    { id: "overdue", label: "Results overdue", overdue: true },
    {
      id: "results_submitted",
      label: "Ratings to apply",
      status: "results_submitted",
    },
    {
      id: "ratings_applied",
      label: "Ratings applied",
      status: "ratings_applied",
    },
    {
      id: "completed_unrated",
      label: "Closed without ratings",
      status: "completed_unrated",
    },
    { id: "revoked", label: "Revoked", status: "revoked" },
  ],
};

export const CHECK_LABELS: Record<
  keyof NonNullable<TournamentSanction["checks"]>,
  { label: string; pass: string; fail: string }
> = {
  club_active: {
    label: "Club",
    pass: "Active club",
    fail: "Club is not active",
  },
  venue_approved: {
    label: "Venue",
    pass: "Approved facility",
    fail: "Facility is not approved",
  },
  referee_assigned: {
    label: "Referee",
    pass: "Tournament referee assigned",
    fail: "No tournament referee",
  },
  has_events: {
    label: "Events",
    pass: "At least one event",
    fail: "No events",
  },
  tournament_active: {
    label: "Published",
    pass: "Published and active",
    fail: "Not published, or cancelled or completed",
  },
  decision_window_open: {
    label: "Timing",
    pass: "Before the last tournament day ends",
    fail: "The tournament is over",
  },
};

export const FACT_LABELS: Record<string, string> = {
  name: "Name",
  tournament_type: "Type",
  start_date: "Start date",
  end_date: "End date",
  facility_id: "Facility",
  facility_name: "Facility",
  venue_name: "Venue",
  venue_address: "Venue address",
  events: "Events",
  referee: "Referee",
};

const DAY = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const DAY_SHORT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});
const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function formatDay(iso: string | null | undefined): string {
  return iso ? DAY.format(new Date(iso)) : "—";
}

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? DATE_TIME.format(new Date(iso)) : "—";
}

/** "Nov 1 – 2, 2026" style range from the submitted start and end dates. */
export function formatDateRange(
  start: string,
  end: string | null | undefined,
): string {
  const startDate = new Date(start);
  if (!end) return DAY.format(startDate);
  const endDate = new Date(end);
  if (startDate.toDateString() === endDate.toDateString()) {
    return DAY.format(startDate);
  }
  return `${DAY_SHORT.format(startDate)} – ${DAY.format(endDate)}`;
}

export function formatRating(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

export function formatDelta(value: number): string {
  const magnitude = formatRating(Math.abs(value));
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `−${magnitude}`;
  return magnitude;
}

export function isRevisionConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    error.code === "SANCTION_REVISION_CONFLICT"
  );
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) return error.message;
  return "That didn't work. Try again.";
}

/** Whether the tournament has started (by its submitted start date). */
export function hasStarted(
  sanction: TournamentSanction,
  now = Date.now(),
): boolean {
  return new Date(sanction.submitted_facts.start_date).getTime() <= now;
}

export const REVOCABLE: readonly TournamentSanctionStatus[] = [
  "requested",
  "changes_requested",
  "sanctioned",
  "results_submitted",
  "completed_unrated",
];

export const CONFIRMABLE: readonly TournamentSanctionStatus[] = [
  "requested",
  "sanctioned",
  "results_submitted",
];

/** Desktop (fine pointer) density from the approved mockups; touch keeps the 44px target. */
export const DENSE_CONTROL = {
  "@media (pointer: fine)": { minHeight: 32 },
} as const;
export const DENSE_ICON = {
  "@media (pointer: fine)": { minHeight: 32, minWidth: 32 },
} as const;
